import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Star,
  Users,
  Shield
} from 'lucide-react';

// All available features that can be assigned to plans
const AVAILABLE_FEATURES = [
  { key: 'orders', name: 'Order Analytics', group: 'Sales & Orders' },
  { key: 'checkout', name: 'Checkout Analysis', group: 'Sales & Orders' },
  { key: 'products', name: 'Product Analytics', group: 'Sales & Orders' },
  { key: 'audit', name: 'SEO Audit', group: 'SEO & Content' },
  { key: 'page-speed', name: 'Page Speed', group: 'SEO & Content' },
  { key: 'product-quality', name: 'Product Quality', group: 'SEO & Content' },
  { key: 'content', name: 'Content Insights', group: 'SEO & Content' },
  { key: 'ai-suggestions', name: 'AI Suggestions', group: 'SEO & Content' },
  { key: 'analytics', name: 'Analytics Portal', group: 'Analytics' },
  { key: 'user-analysis', name: 'User Analysis', group: 'Analytics' },
  { key: 'uptime', name: 'Uptime Monitoring', group: 'System' },
  { key: 'system', name: 'System Information', group: 'System' },
  { key: 'store-config', name: 'Store Config', group: 'System' },
  { key: 'stock-status', name: 'Stock Status', group: 'System' },
  { key: 'admin-users', name: 'Admin Users', group: 'System' },
  { key: 'reports', name: 'PDF Reports', group: 'System' },
];

const PlanManagement = ({ user }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_default: false,
    is_active: true,
    features: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchPlans();
  }, [user]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await fetch('/app/api/admin/plans/list.php', {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFeatureToggle = (featureKey) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureKey)
        ? prev.features.filter(f => f !== featureKey)
        : [...prev.features, featureKey]
    }));
  };

  const handleSelectAllFeatures = () => {
    if (formData.features.length === AVAILABLE_FEATURES.length) {
      setFormData(prev => ({ ...prev, features: [] }));
    } else {
      setFormData(prev => ({ ...prev, features: AVAILABLE_FEATURES.map(f => f.key) }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const endpoint = editingPlan
        ? '/app/api/admin/plans/update.php'
        : '/app/api/admin/plans/create.php';

      const payload = editingPlan
        ? { id: editingPlan.id, ...formData }
        : formData;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchPlans();
        resetForm();
      } else {
        setErrors({ submit: data.message || 'Failed to save plan' });
      }
    } catch (error) {
      setErrors({ submit: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || '',
      is_default: plan.is_default === '1' || plan.is_default === true,
      is_active: plan.is_active === '1' || plan.is_active === true,
      features: plan.features || []
    });
    setShowForm(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;

    try {
      const response = await fetch('/app/api/admin/plans/delete.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: planId })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchPlans();
      } else {
        alert(data.message || 'Failed to delete plan');
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      is_default: false,
      is_active: true,
      features: []
    });
    setEditingPlan(null);
    setShowForm(false);
    setErrors({});
  };

  // Group features by category
  const groupedFeatures = AVAILABLE_FEATURES.reduce((acc, feature) => {
    if (!acc[feature.group]) {
      acc[feature.group] = [];
    }
    acc[feature.group].push(feature);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plan Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage subscription plans with feature access
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Plan
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">
              {editingPlan ? 'Edit Plan' : 'Create New Plan'}
            </h2>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Plan Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="e.g., Basic, Pro, Enterprise"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Brief description of the plan"
                />
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_default"
                  checked={formData.is_default}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Default plan for new users</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
            </div>

            {/* Features Selection */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-gray-700">Features</label>
                <button
                  type="button"
                  onClick={handleSelectAllFeatures}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {formData.features.length === AVAILABLE_FEATURES.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {Object.entries(groupedFeatures).map(([group, features]) => (
                  <div key={group} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">{group}</h4>
                    <div className="space-y-2">
                      {features.map((feature) => (
                        <label key={feature.key} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.features.includes(feature.key)}
                            onChange={() => handleFeatureToggle(feature.key)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{feature.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {errors.submit && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{errors.submit}</div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (editingPlan ? 'Update Plan' : 'Create Plan')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && !showForm ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="bg-white shadow rounded-lg p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-6"></div>
              <div className="space-y-2">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="h-3 bg-gray-200 rounded w-full"></div>
                ))}
              </div>
            </div>
          ))
        ) : plans.length > 0 ? (
          plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white shadow rounded-lg p-6 relative ${
                plan.is_default === '1' || plan.is_default === true ? 'ring-2 ring-blue-500' : ''
              } ${plan.is_active === '0' || plan.is_active === false ? 'opacity-60' : ''}`}
            >
              {/* Badges */}
              <div className="absolute top-4 right-4 flex items-center space-x-2">
                {(plan.is_default === '1' || plan.is_default === true) && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <Star className="h-3 w-3 mr-1" />
                    Default
                  </span>
                )}
                {(plan.is_active === '0' || plan.is_active === false) && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Inactive
                  </span>
                )}
              </div>

              <div className="flex items-center mb-4">
                <CreditCard className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                  {plan.description && (
                    <p className="text-sm text-gray-500">{plan.description}</p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <Users className="h-4 w-4 mr-2" />
                  {plan.users_count || 0} users on this plan
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Shield className="h-4 w-4 mr-2" />
                  {plan.features?.length || 0} features enabled
                </div>
              </div>

              {/* Features List */}
              <div className="border-t border-gray-200 pt-4 mb-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Included Features
                </h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {plan.features?.length > 0 ? (
                    plan.features.map((featureKey) => {
                      const feature = AVAILABLE_FEATURES.find(f => f.key === featureKey);
                      return (
                        <div key={featureKey} className="flex items-center text-sm text-gray-700">
                          <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                          {feature?.name || featureKey}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500 italic">No features enabled</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => handleEdit(plan)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit
                </button>
                {!(plan.is_default === '1' || plan.is_default === true) && (
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No plans found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first subscription plan.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Plan
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanManagement;
